<template lang="pug">
v-card(flat v-scroll="handleScroll")
	v-overlay(absolute :value="$fetchState.pending" color="white" opacity="0.8" align="start")
		div.areas-list--fixed-loader
			v-progress-linear.mb-6(indeterminate striped rounded height="10")
			.title.black--text {{$t('area_list.loading')}}

	v-data-iterator.mb-6(:items="areas" disable-pagination hide-default-footer)
		template(#no-data)
			v-alert.my-6(type="warning" color="accent" text border="bottom")
				div {{$t('area_list.nodata')}}
		template(#item="{item}")
			area-list-item-head.mb-6(v-bind="item.node")
				v-simple-table(v-if="item.node.resorts.nodes.length")
					template(#default)
						thead
							tr
								th.px-2 Dorpen
								th.px-2.text-center(style="width:120px") {{$t('areas_list_item.table_heading.piste')}}
								th.px-2.text-center(style="width:120px") {{$t('areas_list_item.table_heading.skilift')}}
								th.px-2.text-center.text-no-wrap(style="width:80px" v-for="feature in item.node.resorts.nodes[0].features") {{$t(`areas_list_item.table_heading.${feature.type.toLowerCase()}`)}}
								th.px-2.text-center(style="width:120px") {{$t('areas_list_item.table_heading.accommodations')}}
						tbody
							tr(v-for="resort in item.node.resorts.nodes" :class="{disabled: !item.matchingResortIds.includes(resort.id)}")
								td.text-no-wrap
									nuxt-link.mr-1(:to="localePath({name: 'resorts-resort', params: {resort: resort.slug}})" :title="`Wintersport ${resort.name}`") {{resort.name}}
									.caption.grey--text.d-inline ({{resort.elevation}}m)
								td.px-2.text-center
									ws-icon(v-if="resort.hasDownHill" color="success" size="20") check
									span.grey--text(v-else) •
								td.px-2.text-center
									ws-icon(v-if="resort.hasSkiLift" color="success" size="20") check
									span.grey--text(v-else) •
								td.px-2.text-center(v-for="feature in resort.features")
									feature-icon(circle dark small :rating="feature.rating")
								td.px-2.text-center
									nuxt-link(v-if="resort.stats.accommodationCount" :to="localePath({name: 'accommodations-search', hash: `#resorts-${resort.slug}`})")
										.caption.d-inline {{resort.stats.accommodationCount}}
									span.grey--text(v-else) •
				features(:features="selectedFeatures" :area="item.node" :matching-resort-ids="item.matchingResortIds")
			v-divider.hidden-sm-and-up
</template>

<style lang="sass">
.disabled
	opacity: 0.3
.areas-list--fixed-loader
	position: fixed
	top: 40%
	color: black
	margin-top: 100px
	width: 280px
	text-align: center
	margin-left: -140px
</style>

<script>
import gql from 'graphql-tag';
import AreaListItemHead from '@/components/area-list-item-head.vue';
import Features from '@/components/areas/list-item/features.vue';
import ResortTable from '@/components/resort-table.vue';
import debounce from 'lodash/debounce';
const { decodeFilters } = require('@dengl/common/utils/filterstrings');

const FeatureIcon = {
	name: 'FeatureIcon',
	functional: true,
	render(createElement, context) {
		const rating = context.props.rating || parseInt(context.children[0]);
		context.props.color =
			context.props.color ||
			['', 'error', 'warning', 'success', 'success'][rating];
		return createElement('ws-icon', { props: context.props }, [
			['', 'minus', 'tilde', 'plus', 'plus'][rating]
		]);
	}
};

const query = gql`
	query Areas(
		$filters: String
		$limit: Int
		$preferedCity: String!
		$selectedFeatures: [FeatureType]
		$sort: areaSortInput
	) {
		areas: areasElastic(filters: $filters, limit: $limit, sort: $sort) {
			pageInfo {
				totalItems
			}
			edges {
				matchingResortIds
				node {
					id
					name
					slug
					skimap: defaultSkimap {
						url
						alt
					}
					country {
						id
						name
						countryCode
					}
					minElevation
					maxElevation
					reviews {
						average
						voteCount
					}
					features(types: $selectedFeatures) {
						type
						rating
						text
					}
					departures {
						highway {
							distance
						}
						city(city: $preferedCity) {
							distance
							node {
								id
								name
							}
						}
					}
					snowheights(pageInfo: { first: 1 }) {
						nodes {
							snowMountain
							snowValley
						}
					}
					pistes: slopeinfo {
						total
						green_percentage
						blue_percentage
						red_percentage
						black_percentage
					}
					resorts {
						nodes {
							id
							name
							slug
							elevation
							hasSkiLift
							hasDownHill
							isFilterMatch
							features(types: $selectedFeatures) {
								type
								rating
								text
							}
							stats {
								accommodationCount
							}
						}
					}
				}
			}
		}
	}
`;

export default {
	name: 'AreaList',
	components: {
		AreaListItemHead,
		ResortTable,
		FeatureIcon,
		Features
	},
	props: {
		filterstring: {
			type: String,
			default: ''
		},
		sort: {
			type: Object,
			default() {
				return {
					ascending: true,
					field: 'RELEVANCE'
				};
			}
		},
		limit: {
			type: [String, Number],
			default: 30
		},
		hideShowAll: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			newLimit: 0,
			areas: [],
			total: 0
		};
	},
	async fetch() {
		if (this.newLimit) {
			this.newLimit += 30;
		} else {
			this.newLimit = parseInt(this.limit);
		}

		await this.$graphClient
			.query({
				query,
				variables: {
					filters: this.filterstring,
					limit: this.newLimit,
					preferedCity: this.$store.getters.preferedCity,
					selectedFeatures: this.selectedFeatures.map(e => e.toUpperCase()),
					sort: this.sort
				}
			})
			.then(({ data }) => {
				this.$emit('update:total', data.areas.pageInfo.totalItems);
				this.total = data.areas.pageInfo.totalItems;
				this.areas = data.areas.edges;
			});
	},
	computed: {
		selectedFeatures() {
			const filters = decodeFilters(this.filterstring, this.$i18n.locale);
			return filters.features ? filters.features : [];
		}
	},
	watch: {
		filterstring() {
			this.$fetchState.pending = true;
			this.debouncedFetch();
		},
		sort() {
			this.$fetch();
		}
	},
	mounted() {
		this.$emit('update:total', this.total);
	},
	methods: {
		debouncedFetch: debounce(function () {
			this.$fetch();
		}, 200),
		handleScroll(_event, _el) {
			if (
				this.total > this.newLimit &&
				!this.hideShowAll &&
				window.scrollY > document.body.scrollHeight - 2000
			) {
				this.$fetch();
			}
		}
	}
};
</script>
